import React, {useEffect} from 'react';
import {IImportMapping} from "../../models/Extension";
import {DetailsList, Dropdown, IColumn, IDropdownOption, SelectionMode} from "@fluentui/react";
import {clone, zip} from "lodash";

interface IMappingTableProps {
    sampleData: string[][];
    initialMapping: IImportMapping[];
    onChange: (mapping: IImportMapping[]) => void;
    transpose?: boolean;
    vpnIsMfp?: boolean;
}

// Products store weight in pounds and dimensions in inches; the importer converts from the unit picked here.
const weightUnitOptions: IDropdownOption[] = [
    {key: 'lb', text: 'Pounds (lb)'},
    {key: 'oz', text: 'Ounces (oz)'},
    {key: 'kg', text: 'Kilograms (kg)'},
    {key: 'g', text: 'Grams (g)'},
];
const lengthUnitOptions: IDropdownOption[] = [
    {key: 'in', text: 'Inches (in)'},
    {key: 'ft', text: 'Feet (ft)'},
    {key: 'cm', text: 'Centimeters (cm)'},
    {key: 'mm', text: 'Millimeters (mm)'},
    {key: 'm', text: 'Meters (m)'},
];
const unitOptions = (field: string): IDropdownOption[] | undefined => {
    if (field === 'weight') return weightUnitOptions;
    if (field === 'length' || field === 'width' || field === 'height') return lengthUnitOptions;
    return undefined;
};

const MappingTable: React.FC<IMappingTableProps> = (props) => {
    const [sampleData, setSampleData] = React.useState<any[]>([]);
    const [mappings, setMappings] = React.useState<IImportMapping[]>([])
    const [copiedMappings, setCopiedMappings] = React.useState(false);

    useEffect(() => {
        props.onChange(mappings);
    }, [mappings]);

    useEffect(() => {
        if (!copiedMappings && mappings.length === 0 && props.initialMapping.length > 0) {
            setMappings(props.initialMapping);
            setCopiedMappings(true);
        }
    }, [props.initialMapping, copiedMappings]);

    useEffect(() => {
        let raw: (string | undefined)[][] = props.sampleData;
        if (props.transpose) {
            raw = zip(...raw);
        }
        const s: { [colNum: string]: any }[] = raw.map((row) => {
            const data: { [colNum: string]: any } = {};
            for (let i = 0; i < row.length; i++) {
                data[`Row ${i + 1}`] = row[i] ?? '';
            }
            return data;
        });
        setSampleData(s);
    }, [props.sampleData, props.transpose]);

    const fieldMappingOptions: IDropdownOption[] = [
        {key: 'UNMAPPED', text: 'Unmapped'},
        {key: 'vendorPartNumber', text: props.vpnIsMfp ? 'VPN/MFP' : 'VPN'},
        {key: 'manufacturerPartNumber', text: 'MFP', disabled: props.vpnIsMfp},
        {key: 'manufacturer', text: 'Manufacturer'},
        {key: 'name', text: 'Name',},
        {key: 'msrp', text: 'MSRP'},
        {key: 'upc', text: 'UPC'},
        {key: 'cost', text: 'Cost'},
        {key: 'onHand', text: 'On Hand (Quantity)'},
        {key: 'inStock', text: 'In Stock (Boolean)'},
        {key: 'weight', text: 'Weight'},
        {key: 'width', text: 'Width'},
        {key: 'height', text: 'Height'},
        {key: 'length', text: 'Length'},
        {key: 'weightUnit', text: 'Weight Unit (per row)'},
        {key: 'dimensionUnit', text: 'Dimension Unit (per row)'},
        {key: 'description', text: 'Description'},
        {key: 'segment', text: 'Segment'},
        {key: 'category', text: 'Category'},
        {key: 'subCategory', text: 'Sub Category'},
        {key: 'thumbnailUrl', text: 'Thumbnail URL'},
    ];

    const columns: IColumn[] = sampleData.length > 0 ? [
        {
            key: 'ColumnNumber',
            minWidth: 100,
            maxWidth: 100,
            name: 'Column #',
            onRender: (c,i) => <span>{i != null && i+1}</span>
        },
        {
            key: 'Mapping',
            minWidth: 200,
            name: 'Mapping',
            maxWidth: 200,
            onRender: (c: any, i) => (<Dropdown
                options={fieldMappingOptions}
                selectedKey={mappings.find(m => m.column === i)?.field ?? 'UNMAPPED'}
                onChange={(e, d) => {
                    if (i !== undefined) {
                        const newMappings = clone(mappings);
                        const mi = newMappings.findIndex(m => m.column === i);
                        if (mi === -1) {
                            newMappings.push({column: i, field: d?.key.toString() ?? ''});
                        } else {
                            newMappings[mi] = {...newMappings[mi], field: d?.key.toString() ?? '', unit: undefined};
                        }
                        setMappings(newMappings);
                    }
                }}
            />)
        },
        {
            key: 'Unit',
            minWidth: 130,
            name: 'Unit',
            maxWidth: 130,
            onRender: (c: any, i) => {
                const mapping = mappings.find(m => m.column === i);
                const options = mapping ? unitOptions(mapping.field) : undefined;
                if (!mapping || !options) return null;
                return <Dropdown
                    options={options}
                    selectedKey={mapping.unit ?? options[0].key}
                    onChange={(e, d) => {
                        const unit = d?.key.toString();
                        // The first option is the stored unit, so it is saved as "no conversion".
                        setMappings(mappings.map(m => m.column === i
                            ? {...m, unit: unit === options[0].key ? undefined : unit}
                            : m));
                    }}
                />;
            }
        },
        ...Object.keys(sampleData[0]).map(k => ({
            key: k,
            minWidth: 100,
            fieldName: k,
            name: k,
            maxWidth: 200,
            isMultiline: true,
        })),
    ] : [];

    return (
        <DetailsList items={sampleData} columns={columns} selectionMode={SelectionMode.none}/>
    )
}

export default MappingTable;